import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShoppingBag, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function RegisterBuyer() {
  const { register } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", full_name: "", phone: "", city: "", country: "", newsletter_opt_in: false });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...form, role: "buyer" });
      toast.success("SUKANACOOP ✓");
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("login.error"));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-[600px] mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="register-buyer-page">
      <Link to="/inscription" className="inline-flex items-center gap-1 text-sm text-[#4B5563] hover:text-[#1A3322] mb-4" data-testid="back-to-choice">
        <ArrowLeft className="w-4 h-4"/>{t("reg.back")}
      </Link>

      <div className="bg-white p-6 sm:p-8 lg:p-10 rounded-2xl border border-[#1A3322]/10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-full bg-[#F5F5F0] flex items-center justify-center">
            <ShoppingBag className="w-5 h-5 text-[#1A3322]"/>
          </div>
          <div>
            <h1 className="heading font-extrabold text-2xl sm:text-3xl text-[#111812]">{t("reg.b.title")}</h1>
            <p className="text-sm text-[#4B5563] mt-1">{t("reg.b.subtitle")}</p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="buyer-form">
          <div className="space-y-2">
            <Label>{t("reg.b.fullname")} *</Label>
            <Input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="buyer-fullname"/>
          </div>
          <div className="space-y-2">
            <Label>{t("reg.b.email")} *</Label>
            <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="buyer-email"/>
          </div>
          <div className="space-y-2">
            <Label>{t("reg.b.password")} *</Label>
            <Input type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="buyer-password"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>{t("reg.b.city")}</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="buyer-city"/>
            </div>
            <div className="space-y-2">
              <Label>{t("reg.b.country")}</Label>
              <Input value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="buyer-country"/>
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full pill-btn bg-[#1A3322] hover:bg-[#24472F] rounded-full h-11" data-testid="buyer-submit">
            {loading ? t("reg.b.loading") : t("reg.b.submit")}
          </Button>

          <label className="flex items-start gap-2.5 cursor-pointer select-none pt-1" data-testid="buyer-newsletter-label">
            <input
              type="checkbox"
              checked={form.newsletter_opt_in}
              onChange={(e) => set("newsletter_opt_in", e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#1A3322]"
              data-testid="buyer-newsletter-checkbox"
            />
            <span className="text-xs text-[#4B5563] leading-relaxed">
              {t("reg.newsletter")}
            </span>
          </label>

          <p className="text-xs text-[#4B5563] text-center">
            {t("reg.b.terms")} <Link to="/mentions-legales" className="underline">{t("reg.b.terms_link")}</Link>.
          </p>
          <p className="text-sm text-center text-[#4B5563] pt-2">
            {t("reg.already")} <Link to="/connexion" className="text-[#1A3322] font-semibold" data-testid="to-login">{t("header.login")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
